import { useState, useMemo } from 'react';
import {
  paisesConfig,
  top10PaisTypeConfig,
  top10PaisReglas,
  top10PaisGlobalStats,
  top10PaisMap,
  getTop10ByPaisYTipo,
  getEstadosForPais,
  getCiudadesForEstado,
  getEntidadesDisponiblesForPais,
  getTotalEntidadesForPais,
  formatearNumeroPais,
  type PaisId,
  type Top10EntityTypePais,
  type Top10EntryPais,
} from '@/mocks/top10Pais';

const movementConfig: Record<string, { icon: string; color: string; bg: string }> = {
  up: { icon: 'ri-arrow-up-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  down: { icon: 'ri-arrow-down-line', color: 'text-red-500', bg: 'bg-red-50' },
  stable: { icon: 'ri-subtract-line', color: 'text-foreground-400', bg: 'bg-foreground-100' },
};

const trendDots: Record<string, string> = {
  exploding: 'bg-rose-500',
  trending: 'bg-amber-500',
  growing: 'bg-emerald-500',
  active: 'bg-sky-500',
  stable: 'bg-foreground-400',
  declining: 'bg-red-500',
};

function MovementBadge({ movement, value }: { movement: string; value: number }) {
  const cfg = movementConfig[movement];
  return (
    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${cfg.bg} text-xs font-semibold ${cfg.color}`}>
      <i className={`${cfg.icon} text-[10px]`}></i>
      {value}
    </div>
  );
}

function Top10PaisRow({ entry, index, typeColor }: { entry: Top10EntryPais; index: number; typeColor: string }) {
  const movement = movementConfig[entry.movement];
  const dotColor = trendDots[entry.trend];

  return (
    <div className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer group">
      {/* position */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
        index < 3 ? (index === 0 ? 'bg-amber-100 text-amber-700' : index === 1 ? 'bg-slate-100 text-slate-600' : 'bg-orange-100 text-orange-700') : 'bg-background-100 text-foreground-600'
      }`}>
        <span className="text-sm font-black">#{entry.position}</span>
      </div>

      {/* movement */}
      <div className={`flex-shrink-0 w-7 h-7 rounded-lg ${movement.bg} flex items-center justify-center`}>
        <i className={`${movement.icon} ${movement.color} text-xs`}></i>
      </div>

      {/* logo */}
      <img src={entry.logoUrl} alt={entry.entityName} className="w-10 h-10 md:w-12 md:h-12 rounded-xl object-cover flex-shrink-0" loading="lazy" />

      {/* info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</p>
          {entry.isLive && (
            <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
              <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
              EN VIVO
            </span>
          )}
        </div>
        <p className="text-xs text-foreground-500 truncate mt-0.5">{entry.entitySubtitle}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-foreground-400">{entry.ciudad}, {entry.estado}</span>
          <span className="text-[10px] text-foreground-400">{entry.highlight}</span>
        </div>
      </div>

      {/* score */}
      <div className="flex-shrink-0 text-right">
        <p className="text-sm font-bold text-foreground-900">{entry.scoreLabel}</p>
        <div className="flex items-center justify-end gap-2 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
          <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
          </span>
        </div>
      </div>
    </div>
  );
}

type VistaModo = 'por-categoria' | 'todos-los-paises' | 'reglas';

export default function Top10PaisPage() {
  const [activePais, setActivePais] = useState<PaisId>('mx');
  const [activeType, setActiveType] = useState<Top10EntityTypePais>('radios');
  const [activeEstado, setActiveEstado] = useState<string>('');
  const [activeCiudad, setActiveCiudad] = useState<string>('');
  const [viewMode, setViewMode] = useState<VistaModo>('por-categoria');

  const paisInfo = paisesConfig.find(p => p.id === activePais);
  const typeInfo = top10PaisTypeConfig[activeType];
  const estados = getEstadosForPais(activePais);
  const ciudades = activeEstado ? getCiudadesForEstado(activePais, activeEstado) : [];
  const availableTypes = getEntidadesDisponiblesForPais(activePais);
  const totalForPais = getTotalEntidadesForPais(activePais);

  const currentEntries = useMemo(() => {
    let entries = getTop10ByPaisYTipo(activePais, activeType);
    if (activeEstado) {
      entries = entries.filter(e => e.estado === activeEstado);
    }
    if (activeCiudad) {
      entries = entries.filter(e => e.ciudad === activeCiudad);
    }
    return entries;
  }, [activePais, activeType, activeEstado, activeCiudad]);

  const enVivoEntries = useMemo(() => currentEntries.filter(e => e.isLive), [currentEntries]);
  const topRisingEntries = useMemo(() => [...currentEntries].sort((a, b) => b.scoreChange - a.scoreChange).slice(0, 10), [currentEntries]);

  // combined view: all countries, current type
  const allPaisesEntries = useMemo(() => {
    return paisesConfig.flatMap(pais => {
      const map = top10PaisMap[pais.id];
      if (!map || !map[activeType]) return [];
      return map[activeType].slice(0, 5).map(e => ({ ...e, paisId: pais.id, paisFlag: pais.flag }));
    });
  }, [activeType]);

  const handlePaisChange = (pais: PaisId) => {
    setActivePais(pais);
    setActiveEstado('');
    setActiveCiudad('');
    const types = getEntidadesDisponiblesForPais(pais);
    if (types.length > 0 && !types.includes(activeType)) {
      setActiveType(types[0]);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Sophisticated%20dark%20world%20map%20data%20visualization%20center%20with%20warm%20emerald%20green%20and%20teal%20accent%20lights%20highlighting%20different%20continents%20and%20regional%20hubs%2C%20floating%20holographic%20country%20flags%20circling%20a%20central%20ranking%20podium%2C%20global%20competition%20atmosphere%20with%20interconnected%20glowing%20network%20nodes%20spanning%20across%20the%20map%2C%20clean%20modern%20analytics%20center%20aesthetic%2C%20cinematic%20wide-angle%20shot%20with%20dramatic%20atmospheric%20depth%20and%20soft%20volumetric%20lighting%2C%20no%20text%20or%20labels%20visible%2C%20abstract%20geographic%20data%20command%20center%20photography&width=1600&height=900&seq=top10-pais-hero-2026&orientation=landscape"
          alt="Top 10 por País"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 w-full max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/30 backdrop-blur-sm border border-emerald-400/30 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-xs font-bold text-emerald-200 whitespace-nowrap">COMPETENCIA REGIONAL</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-4 drop-shadow-lg">
              Top 10 por País
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto">
              Rankings locales con filtros de país, estado y ciudad. Los usuarios pueden ver rankings globales o locales — la competencia es regional pero la visibilidad es total.
            </p>

            {/* hero stats overlay */}
            <div className="inline-flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-6 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/10">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10PaisGlobalStats.totalPaises}</p>
                <p className="text-[10px] text-white/60">Países</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10PaisGlobalStats.totalEstados}</p>
                <p className="text-[10px] text-white/60">Estados</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10PaisGlobalStats.totalCiudades}</p>
                <p className="text-[10px] text-white/60">Ciudades</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10PaisGlobalStats.totalEntidadesRankeadas}</p>
                <p className="text-[10px] text-white/60">Entidades</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* sticky bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            {/* back + title */}
            <div className="flex items-center gap-3">
              <a
                href="/"
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
              >
                <i className="ri-arrow-left-line text-sm"></i>
              </a>
              <div className="flex items-center gap-2">
                <i className="ri-earth-line text-emerald-500"></i>
                <span className="text-sm font-bold text-foreground-900">Top 10 por País</span>
              </div>
            </div>

            {/* view mode pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {([
                { id: 'por-categoria' as VistaModo, label: 'Por País', icon: 'ri-flag-line' },
                { id: 'todos-los-paises' as VistaModo, label: 'Todos los Países', icon: 'ri-global-line' },
                { id: 'reglas' as VistaModo, label: 'Reglas', icon: 'ri-scales-line' },
              ] as const).map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    viewMode === mode.id
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${mode.icon} text-[11px]`}></i>
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Por País view */}
        {viewMode === 'por-categoria' && (
          <>
            {/* country tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {paisesConfig.map((pais) => (
                <button
                  key={pais.id}
                  onClick={() => handlePaisChange(pais.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    activePais === pais.id
                      ? `${pais.bg} ${pais.color} ring-2 ring-offset-2 ring-emerald-300`
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <span className="text-lg">{pais.flag}</span>
                  {pais.nombre}
                </button>
              ))}
            </div>

            {/* pais description */}
            {paisInfo && (
              <div className="mb-5 p-4 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{paisInfo.flag}</span>
                  <div>
                    <h2 className="text-base font-bold text-foreground-900">{paisInfo.nombre}</h2>
                    <p className="text-xs text-foreground-500">{paisInfo.totalRadios} radios · {paisInfo.totalEntidades} entidades totales</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600">{paisInfo.description}</p>
              </div>
            )}

            {/* type tabs */}
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              {availableTypes.map((type) => {
                const info = top10PaisTypeConfig[type];
                const count = getTop10ByPaisYTipo(activePais, type).length;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveType(type)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activeType === type
                        ? `${info.bg} ${info.color}`
                        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${info.icon} text-sm`}></i>
                    {info.label}
                    <span className="text-xs opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* geo filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5 p-3 rounded-xl bg-background-100 border border-background-200/70">
              {/* estado */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-foreground-500 font-semibold mr-1">Estado:</span>
                <button
                  onClick={() => { setActiveEstado(''); setActiveCiudad(''); }}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    !activeEstado ? 'bg-foreground-800 text-background-50' : 'text-foreground-500 hover:text-foreground-700 bg-background-50 border border-background-200'
                  }`}
                >
                  Todos
                </button>
                {estados.map((est) => (
                  <button
                    key={est}
                    onClick={() => { setActiveEstado(est); setActiveCiudad(''); }}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activeEstado === est ? 'bg-foreground-800 text-background-50' : 'text-foreground-500 hover:text-foreground-700 bg-background-50 border border-background-200'
                    }`}
                  >
                    {est}
                  </button>
                ))}
              </div>

              {/* ciudad (only shown when estado is selected) */}
              {activeEstado && ciudades.length > 0 && (
                <>
                  <span className="hidden sm:block text-foreground-300">|</span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-foreground-500 font-semibold mr-1">Ciudad:</span>
                    <button
                      onClick={() => setActiveCiudad('')}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                        !activeCiudad ? 'bg-foreground-800 text-background-50' : 'text-foreground-500 hover:text-foreground-700 bg-background-50 border border-background-200'
                      }`}
                    >
                      Todas
                    </button>
                    {ciudades.map((ciu) => (
                      <button
                        key={ciu}
                        onClick={() => setActiveCiudad(ciu)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                          activeCiudad === ciu ? 'bg-foreground-800 text-background-50' : 'text-foreground-500 hover:text-foreground-700 bg-background-50 border border-background-200'
                        }`}
                      >
                        {ciu}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* results count */}
              <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">{currentEntries.length} resultados</span>
            </div>

            {/* en vivo quick toggle */}
            {enVivoEntries.length > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                  <span className="text-xs font-semibold text-red-600">{enVivoEntries.length} en vivo</span>
                </div>
              </div>
            )}

            {/* podium */}
            {currentEntries.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[1, 0, 2].map((idx) => {
                  const entry = currentEntries[idx];
                  if (!entry) return null;
                  const movement = movementConfig[entry.movement];
                  const placements = [
                    { label: 'ORO', color: 'from-amber-400 to-yellow-500', textColor: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
                    { label: 'PLATA', color: 'from-slate-400 to-slate-500', textColor: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
                    { label: 'BRONCE', color: 'from-orange-400 to-orange-600', textColor: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
                  ];
                  const placement = placements[idx];

                  return (
                    <div key={entry.entityName} className={`relative p-5 rounded-2xl border ${placement.border} ${placement.bg} cursor-pointer hover:shadow-sm transition-all`}>
                      <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-md bg-gradient-to-r ${placement.color} text-white text-[10px] font-bold`}>
                        {placement.label}
                      </div>

                      <div className="flex items-center gap-3 mb-4">
                        <img src={entry.logoUrl} alt="" className="w-14 h-14 rounded-xl object-cover ring-2 ring-white/60" loading="lazy" />
                        <div className="min-w-0">
                          <p className="text-base font-bold text-foreground-900 truncate">{entry.entityName}</p>
                          <p className="text-xs text-foreground-500 truncate">{entry.entitySubtitle}</p>
                          <p className="text-[10px] text-foreground-400">{entry.ciudad}, {entry.estado}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xl font-black text-foreground-800">#{entry.position}</span>
                        <MovementBadge movement={entry.movement} value={entry.movementValue} />
                        <span className={`text-sm font-bold ${typeInfo.color}`}>{entry.scoreLabel}</span>
                        <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                        </span>
                        {entry.isLive && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            EN VIVO
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* rest of top 10 */}
            <div className="space-y-2">
              {currentEntries.slice(3, 10).map((entry, i) => (
                <Top10PaisRow key={entry.entityName} entry={entry} index={i + 3} typeColor={typeInfo.color} />
              ))}
            </div>

            {currentEntries.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-map-pin-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-sm text-foreground-500">No hay entidades rankeadas en esta combinación de filtros</p>
                <button
                  onClick={() => { setActiveEstado(''); setActiveCiudad(''); }}
                  className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-semibold hover:bg-background-200/50 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  Limpiar filtros
                </button>
              </div>
            )}
          </>
        )}

        {/* Todos los Países view */}
        {viewMode === 'todos-los-paises' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-global-line text-emerald-500"></i>
                Todas las Regiones — {typeInfo.label}
              </h2>
              <p className="text-sm text-foreground-600">Top 5 de {typeInfo.label} en cada país. Compará rankings entre regiones.</p>
            </div>

            {/* type selector */}
            <div className="flex flex-wrap items-center gap-1.5 mb-6">
              {(Object.keys(top10PaisTypeConfig) as Top10EntityTypePais[]).map((type) => {
                const info = top10PaisTypeConfig[type];
                return (
                  <button
                    key={type}
                    onClick={() => setActiveType(type)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activeType === type ? `${info.bg} ${info.color}` : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${info.icon} text-sm`}></i>
                    {info.label}
                  </button>
                );
              })}
            </div>

            {/* per country sections */}
            <div className="space-y-8">
              {paisesConfig.map((pais) => {
                const entries = (top10PaisMap[pais.id]?.[activeType] || []).slice(0, 5);
                const typeInfoPais = top10PaisTypeConfig[activeType];
                if (entries.length === 0) return null;

                return (
                  <div key={pais.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        onClick={() => { handlePaisChange(pais.id); setViewMode('por-categoria'); }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold whitespace-nowrap transition-all cursor-pointer ${pais.bg} ${pais.color} hover:ring-2 hover:ring-offset-1 hover:ring-emerald-300`}
                      >
                        <span className="text-base">{pais.flag}</span>
                        {pais.nombre}
                      </button>
                      <span className="text-xs text-foreground-500">{entries.length} entidades</span>
                    </div>

                    <div className="space-y-2">
                      {entries.map((entry, i) => {
                        const movement = movementConfig[entry.movement];
                        return (
                          <div key={entry.entityName} className="flex items-center gap-3 md:gap-4 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer">
                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                              <span className={`text-sm font-bold ${i < 3 ? typeInfoPais.color : 'text-foreground-600'}`}>#{entry.position}</span>
                            </div>
                            <div className={`flex-shrink-0 w-6 h-6 rounded-md ${movement.bg} flex items-center justify-center`}>
                              <i className={`${movement.icon} ${movement.color} text-xs`}></i>
                            </div>
                            <img src={entry.logoUrl} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</p>
                              <p className="text-xs text-foreground-500 truncate">{entry.ciudad}, {entry.estado}</p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <p className="text-sm font-bold text-foreground-900">{entry.scoreLabel}</p>
                              <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Reglas view */}
        {viewMode === 'reglas' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-scales-line text-violet-500"></i>
                Reglas del Sistema
              </h2>
              <p className="text-sm text-foreground-600">Las 4 reglas que gobiernan el Top 10 por País. Rankings regionales con métricas locales.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {top10PaisReglas.map((regla) => (
                <div key={regla.id} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${regla.gradiente} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${regla.icono} text-white text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground-900 mb-2">{regla.titulo}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{regla.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* stats summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/70">
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalPaises}</p>
                <p className="text-xs text-foreground-500">Países</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalEstados}</p>
                <p className="text-xs text-foreground-500">Estados</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalCiudades}</p>
                <p className="text-xs text-foreground-500">Ciudades</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalEntidadesRankeadas}</p>
                <p className="text-xs text-foreground-500">Entidades</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.actualizacionesPorHora}/h</p>
                <p className="text-xs text-foreground-500">Actualización</p>
              </div>
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/top-10-global"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-bold hover:bg-background-200/50 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-global-line"></i>
            Ver Top 10 Global
          </a>
          <a
            href="/rankings"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-bold hover:bg-background-200/50 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bar-chart-line"></i>
            Explorar Rankings
          </a>
          <a
            href="/tv-app"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold hover:from-emerald-600 hover:to-teal-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-tv-line"></i>
            Ver en TV App
          </a>
        </div>
      </div>
    </div>
  );
}